/**
 * productSearchClient.ts
 *
 * Calls our server's unified product search endpoint, scoped to a
 * marketplace + shop:
 *   POST {SERVER_URL}/v2/:platform/:shop_id/products
 *
 * Returns the same envelope as `GET /api/v2/product_listings`
 * (snake_case `ProductListingApiResponse` rows + `_meta.source`). Each
 * row is mapped through `ProductListingService.transformApiProduct` so
 * the picker UI receives the same camelCase `Product` shape the
 * management page uses.
 *
 * Same auth contract as messageApiClient — Bearer JWT from `getAccess()`.
 */

import axios from "axios"
import { getAccess, clearTokenCache } from "src/db/serviceApiSetting"
import { env } from "src/config/env"
import ProductListingService from "src/app/modules/product/services/productListingService"
import type { Product } from "src/app/modules/product/types/ProductListing"

export type PickerPlatform = "tiktok" | "shopee" | "lazada" | "whatsapp"

export interface ProductSearchRequest {
  title?: string
  sku?: string
  limit?: number
  cursor?: string | null
}

export interface ProductSearchResult {
  products: Product[]
  source: "sync" | "live"
  /** Opaque cursor for the next page; null at end-of-stream. */
  nextCursor: string | null
  /** Mirrors `nextCursor !== null` — convenience for the picker. */
  hasMore: boolean
}

class ProductSearchClient {
  private static instance: ProductSearchClient

  static getInstance(): ProductSearchClient {
    if (!this.instance) {
      this.instance = new ProductSearchClient()
    }
    return this.instance
  }

  async searchProducts(
    platform: PickerPlatform,
    shopId: string,
    request: ProductSearchRequest = {}
  ): Promise<ProductSearchResult> {
    const apiAccess = await getAccess()
    if (!apiAccess?.accessToken) {
      throw new Error("Failed to retrieve access token")
    }

    const url = `${env.SERVER_URL}/v2/${platform}/${shopId}/products`

    try {
      const response = await axios.post<{
        data: any[]
        next_cursor?: string | null
        has_more?: boolean
        _meta?: { source?: "sync" | "live" }
      }>(url, request, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiAccess.accessToken}`,
        },
      })

      const transformer = ProductListingService.getInstance()
      const products = (response.data.data ?? []).map((row) =>
        transformer.transformApiProduct(row)
      )

      // Normalize: treat empty string `next_cursor` as no-more-pages.
      // Server already does this, but be defensive against intermediaries.
      const rawCursor = response.data.next_cursor
      const nextCursor =
        rawCursor && rawCursor.length > 0 ? rawCursor : null
      return {
        products,
        source: response.data._meta?.source ?? "live",
        nextCursor,
        // Derive hasMore from cursor truthiness — single source of truth.
        hasMore: nextCursor !== null,
      }
    } catch (error: any) {
      if (error?.response?.status === 401) {
        clearTokenCache()
      }
      const message =
        error?.response?.data?.error?.message ||
        `Failed to search ${platform} products: ${error?.response?.status ?? "network error"}`
      throw new Error(message)
    }
  }
}

export default ProductSearchClient.getInstance()
